
import React from "react";
import { AIStaffMember } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import StaffAchievements from "./StaffAchievements";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";

interface StaffProfileProps {
  aiStaff: AIStaffMember;
  staffId?: string;
}

const StaffProfile: React.FC<StaffProfileProps> = ({ aiStaff, staffId }) => {
  const isMobile = useIsMobile();
  
  const getLevelColor = (level: string) => {
    switch (level) {
      case 'intern': return 'bg-slate-500';
      case 'junior': return 'bg-blue-500';
      case 'senior': return 'bg-green-500';
      case 'director': return 'bg-amber-500';
      default: return 'bg-slate-500';
    }
  };
  
  const formatLevel = (level: string) => {
    return level.charAt(0).toUpperCase() + level.slice(1);
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="grid gap-8 grid-cols-1 md:grid-cols-3">
          {/* Staff Profile Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
                {aiStaff.name?.charAt(0) || 'A'}
              </div>
              <div>
                <h3 className="text-xl font-semibold">{aiStaff.name}</h3>
                <div className="flex flex-wrap gap-2 mt-1">
                  <Badge variant="secondary" className="capitalize">
                    {aiStaff.role.replace('-', ' ')}
                  </Badge>
                  <Badge className={`capitalize ${getLevelColor(aiStaff.level)}`}>
                    {formatLevel(aiStaff.level)}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-sm text-muted-foreground mb-1">Experience</div>
              <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-400 to-purple-500 rounded-full"
                  style={{ width: `${Math.min((aiStaff.experience / 1000) * 100, 100)}%` }}
                ></div>
              </div>
              <div className="text-xs text-right mt-1 text-muted-foreground">
                {aiStaff.experience} / 1000 XP
              </div>
            </div>
          </div>
          
          {/* Skills & Abilities Section */}
          <div>
            <h4 className="font-medium mb-3">Abilities</h4>
            <div className="flex flex-wrap gap-1.5">
              {aiStaff.abilities.map((ability, index) => (
                <Badge key={index} variant="outline" className="whitespace-nowrap">
                  {ability}
                </Badge>
              ))}
            </div>
          </div>
          
          {/* Achievements Section */}
          <StaffAchievements achievements={aiStaff.achievements} />
        </div>
      </CardContent>
    </Card>
  );
};

export default StaffProfile;
